import React, { useEffect, useMemo, useRef, useState } from "react";
import "./bill-list.style.scss";

// helpers
import moment from "moment";
import { fromAdToBs } from "../../global/function";
import { handleActivationKey } from "../../global/keyboardActivation";

// icons
import { FaGrip, FaPrint, FaTableList } from "react-icons/fa6";

// components
import EmptyRecord from "../empty-record/empty-record.component";
import BillBulkPrint from "./bill-bulk-print.component";
import BillListItem from "./bill-list-item.component";
import InlineNotice from "../inline-notice/inline-notice.component";
import DataTable from "../data-table/data-table.component";
import SelectBox from "../form-inputs/select-box.component";

// data
import { billTableColumns } from "./bill-table-columns";
import { BILL_SORT_OPTIONS } from "./bill-filter.types";

// queries
import { useInfiniteBills } from "../../queries/bill.queries";
import { useInfiniteScrollSentinel } from "../../hooks/useInfiniteScrollSentinel";

// types
import type {
	Bill,
	BillSearchFilters,
	BillSortOrder,
} from "../../queries/bill.queries";
import type { BillStatusFilter } from "./bill-filter.types";
import type { BillViewMode } from "../../hooks/useLastBillViewMode";

function billMonth(date: string) {
	const enMonth = moment(date).format("MMMM");
	const nepMonth = fromAdToBs(date, "MMMM");
	return `${enMonth}/${nepMonth}`;
}

interface BillGroup {
	key: string;
	monthKey: string;
	label: string;
	bills: Bill[];
}

// Groups CONSECUTIVE bills sharing the same AD month/year — safe because
// bill/add always stamps invoice_date as "now" at creation time
// (billing_system_BE controllers/bill.controller.js's addBill), never
// backdated, so the list's existing sort (invoice_number DESC) already tracks
// invoice_date DESC closely enough that same-month bills land adjacent to
// each other; this never re-sorts, just partitions the list as-is.
// monthKey drives the adjacency check; the same month can still recur as a
// separate, non-consecutive group when the sort order and invoice_date
// diverge slightly, so `key` (the React key) is disambiguated with the
// group's index rather than reusing monthKey, which could collide.
function groupBillsByMonth(bills: Bill[]): BillGroup[] {
	const groups: BillGroup[] = [];
	for (const bill of bills) {
		const monthKey = moment(bill.invoice_date).format("YYYY-MM");
		const current = groups[groups.length - 1];
		if (current && current.monthKey === monthKey) {
			current.bills.push(bill);
		} else {
			groups.push({
				key: `${monthKey}-${groups.length}`,
				monthKey,
				label: `${moment(bill.invoice_date).format("MMMM YYYY")} | ${fromAdToBs(bill.invoice_date, "MMMM YYYY")}`,
				bills: [bill],
			});
		}
	}
	return groups;
}

interface BillListProps {
	selectedBillId?: string;
	onSelectBill: (bill: Bill | Record<string, never>) => void;
	billFilterValues: BillSearchFilters;
	statusFilter: BillStatusFilter;
	onStatusFilterChange: (statusFilter: BillStatusFilter) => void;
	sortOrder: BillSortOrder;
	onSortOrderChange: (sortOrder: BillSortOrder) => void;
	viewMode: BillViewMode;
	onViewModeChange: (viewMode: BillViewMode) => void;
}

function BillList({
	selectedBillId,
	onSelectBill,
	billFilterValues,
	statusFilter,
	onStatusFilterChange,
	sortOrder,
	onSortOrderChange,
	viewMode,
	onViewModeChange,
}: BillListProps) {
	const [bulkPrint, setBulkPrint] = useState(false);
	const [printSuccessNotice, setPrintSuccessNotice] = useState<string | null>(
		null,
	);

	const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
		useInfiniteBills(billFilterValues);

	const billList = useMemo(
		() => data?.pages.flatMap((page) => page.bill) ?? [],
		[data],
	);
	const stats = data?.pages[0];

	const sentinelRef = useInfiniteScrollSentinel(() => fetchNextPage(), {
		enabled: !!hasNextPage && !isFetchingNextPage,
	});

	const billMonths = useMemo(() => {
		if (
			billFilterValues?.fiscal_year === "" &&
			billFilterValues?.date_from &&
			billFilterValues?.date_to
		) {
			return {
				start: billMonth(billFilterValues.date_from),
				end: billMonth(billFilterValues.date_to),
			};
		}
		return undefined;
	}, [
		billFilterValues?.fiscal_year,
		billFilterValues?.date_from,
		billFilterValues?.date_to,
	]);

	const hasDateFilter = !!(
		billFilterValues?.date_from && billFilterValues?.date_to
	);

	// Fiscal-year filtering makes month grouping meaningless (a fiscal year
	// spans 12 months of mixed AD years) — falls back to a flat list there,
	// same as the header's own fiscal-year-vs-date-range display switch above.
	const isGrouped = !billFilterValues?.fiscal_year;
	const groups = useMemo(
		() => (isGrouped ? groupBillsByMonth(billList) : null),
		[billList, isGrouped],
	);

	// Month sub-headers stick right below the section header — measured
	// dynamically (not a guessed constant) since the header's own height
	// varies with how many stat boxes/whether the fiscal-year row renders.
	const sectionHeaderRef = useRef<HTMLDivElement>(null);
	const [sectionHeaderHeight, setSectionHeaderHeight] = useState(0);
	useEffect(() => {
		const el = sectionHeaderRef.current;
		if (!el) return;
		// ResizeObserver only signals "something changed, go re-measure" here —
		// its own contentRect is content-box only (excludes this header's 24px
		// padding), so the actual number comes from getBoundingClientRect
		// (border-box) instead, matching what position: sticky itself uses.
		const observer = new ResizeObserver(() => {
			setSectionHeaderHeight(el.getBoundingClientRect().height);
		});
		observer.observe(el);
		return () => observer.disconnect();
	}, []);
	const monthHeaderTop = `calc(${sectionHeaderHeight}px)`;

	const loadMoreSentinel = (hasNextPage || isFetchingNextPage) && (
		<div ref={sentinelRef} className="bill-list-load-more">
			{isFetchingNextPage && "Loading more bills..."}
		</div>
	);

	return (
		<div className="bill-list-container">
			<div
				className={`bill-section-header ${viewMode === "table" ? "table-view" : ""}`}
				ref={sectionHeaderRef}
			>
				<div className="bill-section-header-inner">
					<div className="header-title">
						<h2>Generated Bills</h2>
						<div className="header-title-actions">
							<div
								className="view-mode-toggle"
								role="tablist"
								aria-label="Bill list view"
							>
								<div
									className={`view-mode-btn ${viewMode === "card" ? "active" : ""}`}
									role="tab"
									aria-selected={viewMode === "card"}
									tabIndex={viewMode === "card" ? -1 : 0}
									title="Card view"
									onClick={() => onViewModeChange("card")}
									onKeyDown={(e) =>
										handleActivationKey(e, () =>
											onViewModeChange("card"),
										)
									}
								>
									<FaGrip />
								</div>
								<div
									className={`view-mode-btn ${viewMode === "table" ? "active" : ""}`}
									role="tab"
									aria-selected={viewMode === "table"}
									tabIndex={viewMode === "table" ? -1 : 0}
									title="Table view"
									onClick={() => onViewModeChange("table")}
									onKeyDown={(e) =>
										handleActivationKey(e, () =>
											onViewModeChange("table"),
										)
									}
								>
									<FaTableList />
								</div>
							</div>
							{billList.length > 0 && (
								<div
									className="print-all-btn"
									onClick={() => setBulkPrint(true)}
								>
									<FaPrint />
									Print All
								</div>
							)}
						</div>
					</div>

					<div className="separator"></div>

					<div
						className="header-info-container"
						role="tablist"
						aria-label="Filter bills by status"
					>
						<div
							className={`header-info header-info--pill ${statusFilter === "all" ? "active" : ""}`}
							role="tab"
							aria-selected={statusFilter === "all"}
							tabIndex={statusFilter === "all" ? -1 : 0}
							onClick={() => onStatusFilterChange("all")}
							onKeyDown={(e) =>
								handleActivationKey(e, () =>
									onStatusFilterChange("all"),
								)
							}
						>
							<span className="header-info-pill-label">
								All Bills
							</span>
						</div>
						<div
							className={`header-info header-info--pill ${statusFilter === "bill" ? "active" : ""}`}
							role="tab"
							aria-selected={statusFilter === "bill"}
							tabIndex={statusFilter === "bill" ? -1 : 0}
							onClick={() => onStatusFilterChange("bill")}
							onKeyDown={(e) =>
								handleActivationKey(e, () =>
									onStatusFilterChange("bill"),
								)
							}
						>
							<h3 style={{ color: "#3498DB" }}>
								{stats?.totalBill}
							</h3>
							<span>Bill Invoices</span>
						</div>
						<div
							className={`header-info header-info--pill ${statusFilter === "credit" ? "active" : ""}`}
							role="tab"
							aria-selected={statusFilter === "credit"}
							tabIndex={statusFilter === "credit" ? -1 : 0}
							onClick={() => onStatusFilterChange("credit")}
							onKeyDown={(e) =>
								handleActivationKey(e, () =>
									onStatusFilterChange("credit"),
								)
							}
						>
							<h3 style={{ color: "#d47c09ff" }}>
								{stats?.totalCreditNote}
							</h3>
							<span>Credit Notes</span>
						</div>
						{(!!stats?.totalFailed ||
							statusFilter === "failed") && (
							<div
								className={`header-info header-info--pill ${statusFilter === "failed" ? "active" : ""}`}
								role="tab"
								aria-selected={statusFilter === "failed"}
								tabIndex={statusFilter === "failed" ? -1 : 0}
								onClick={() => onStatusFilterChange("failed")}
								onKeyDown={(e) =>
									handleActivationKey(e, () =>
										onStatusFilterChange("failed"),
									)
								}
							>
								<h3 style={{ color: "#d35a47" }}>
									{stats?.totalFailed}
								</h3>
								<span>Unsynced bills</span>
							</div>
						)}
						{(!!stats?.totalCanceled ||
							statusFilter === "canceled") && (
							<div
								className={`header-info header-info--pill ${statusFilter === "canceled" ? "active" : ""}`}
								role="tab"
								aria-selected={statusFilter === "canceled"}
								tabIndex={statusFilter === "canceled" ? -1 : 0}
								onClick={() => onStatusFilterChange("canceled")}
								onKeyDown={(e) =>
									handleActivationKey(e, () =>
										onStatusFilterChange("canceled"),
									)
								}
							>
								<h3 style={{ color: "#9D9D9D" }}>
									{stats?.totalCanceled}
								</h3>
								<span>Canceled</span>
							</div>
						)}
					</div>
					<div className="bill-date-info">
						<div className="bill-date-info-dates">
							{billFilterValues?.fiscal_year ? (
								<div className="date-info-container">
									<div className="label-info">
										{billFilterValues.fiscal_year}
									</div>
									<div className="label">Fiscal Year</div>
								</div>
							) : hasDateFilter ? (
								<>
									<div className="date-info-container">
										<div className="label-info">
											{billMonths?.start}
										</div>
										<div className="label">Bills From</div>
									</div>
									<div className="date-separator"></div>
									<div className="date-info-container">
										<div className="label-info">
											{billMonths?.end}
										</div>
										<div className="label">Bills To</div>
									</div>
								</>
							) : (
								<div className="date-info-container">
									<div className="label-info">All Bills</div>
									<div className="label">No date filter</div>
								</div>
							)}
						</div>
						<div className="bill-date-info-sort">
							<SelectBox
								aria-label="Sort bills"
								options={BILL_SORT_OPTIONS}
								value={sortOrder}
								onChange={(e) =>
									onSortOrderChange(
										e.target.value as BillSortOrder,
									)
								}
							/>
						</div>
					</div>
				</div>
			</div>
			<InlineNotice
				message={printSuccessNotice}
				type="success"
				autoHideMs={4000}
				onDismiss={() => setPrintSuccessNotice(null)}
			/>
			<div
				className={`bill-list-items ${viewMode === "table" ? "table-view" : ""}`}
			>
				{billList.length > 0 ? (
					viewMode === "table" ? (
						// Flat, ungrouped — unlike card view, every row already
						// has its own Date column, and the materialized-book
						// view this table takes cues from wasn't grouped either.
						<DataTable
							columns={billTableColumns}
							data={billList}
							idKey="bill_id"
							onRowClick={onSelectBill}
							selectedRowId={selectedBillId}
						/>
					) : groups ? (
						groups.map((group) => (
							<React.Fragment key={group.key}>
								<div
									className="bill-month-header"
									style={{ top: monthHeaderTop }}
								>
									{group.label}
								</div>
								{group.bills.map((bill) => (
									<BillListItem
										key={bill.bill_id}
										bill={bill}
										selected={
											selectedBillId === bill.bill_id
										}
										onSelect={onSelectBill}
									/>
								))}
							</React.Fragment>
						))
					) : (
						billList.map((bill) => (
							<BillListItem
								key={bill.bill_id}
								bill={bill}
								selected={selectedBillId === bill.bill_id}
								onSelect={onSelectBill}
							/>
						))
					)
				) : isLoading ? (
					<EmptyRecord caption="Loading bills..." />
				) : (
					<EmptyRecord />
				)}
				{loadMoreSentinel}
			</div>

			<BillBulkPrint
				isOpen={bulkPrint}
				onBulkPrintModalClose={() => setBulkPrint(false)}
				onPrintSuccess={() =>
					setPrintSuccessNotice("PDF opened in a new tab.")
				}
				filterValues={billFilterValues}
				onSelectBill={onSelectBill}
			/>
		</div>
	);
}

export default BillList;
